import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Settings, Monitor, Users, MessageSquare, ChevronDown, Share2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const MeetRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [connectionState, setConnectionState] = useState('connecting');
  const [showRelayWarning, setShowRelayWarning] = useState(false);
  const [isWaitingForPeer, setIsWaitingForPeer] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Diagnostic states
  const [selectedPath, setSelectedPath] = useState('—');
  const [downlink, setDownlink] = useState('—');
  const [networkType, setNetworkType] = useState('—');
  const [forceTurn, setForceTurn] = useState(false);
  const [blockRelay] = useState(true);
  const [diagnosticLogs, setDiagnosticLogs] = useState([]);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const webSocketRef = useRef(null);
  const statsIntervalRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  useEffect(() => {
    initializeCall();
    updateNetworkInfo();
    
    const networkInterval = setInterval(updateNetworkInfo, 2000);
    
    return () => {
      clearInterval(networkInterval);
      cleanup();
    };
  }, [roomId]);

  // Auto-hide controls after 3 seconds of inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);
  
  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDiagnosticLogs(prev => [...prev, `[${timestamp}] ${message}`].slice(-50));
  };
  
  const updateNetworkInfo = () => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      setDownlink(conn.downlink ? `${conn.downlink} Mbps` : '—');
      setNetworkType(conn.effectiveType || conn.type || '—');
      
      if (conn.downlink && conn.downlink < 1) {
        addLog(`⚠️ Warning: Very low downlink speed: ${conn.downlink} Mbps`);
      }
    }
  };

  const initializeCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      connectWebSocket();
      
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Failed to access camera/microphone');
    }
  };

  const connectWebSocket = () => {
    // Get backend URL from environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    // Convert http/https to ws/wss
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/room/${roomId}`;
    
    addLog(`Connecting to: ${wsUrl}`);
    const ws = new WebSocket(wsUrl);
    webSocketRef.current = ws;

    ws.onopen = () => {
      addLog('WebSocket connected');
      setConnectionState('connected');
    };

    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);

      switch (message.type) {
        case 'room-joined':
          if (message.participants === 1) {
            setIsWaitingForPeer(true);
          } else {
            setIsWaitingForPeer(false);
            await createPeerConnection();
            await createOffer();
          }
          break;
          
        case 'user-joined':
          setIsWaitingForPeer(false);
          break;
          
        case 'offer':
          await handleOffer(message.offer);
          break;
          
        case 'answer':
          await handleAnswer(message.answer);
          break;
          
        case 'ice-candidate':
          await handleIceCandidate(message.candidate);
          break;
          
        case 'user-left':
          handlePeerLeft();
          break;
          
        case 'policy-block':
          addLog(`⛔ Peer reported policy violation: ${message.reason}`);
          toast.error('Call blocked: ' + (message.reason || 'Policy violation'));
          setShowRelayWarning(true);
          cleanup();
          break;
          
        case 'error':
          toast.error(message.message);
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
    };

    ws.onclose = () => {
      addLog('WebSocket closed');
      setConnectionState('disconnected');
    };
  };

  const createPeerConnection = async () => {
    const configuration = {
      iceServers: [
        { urls: ['stun:stun.l.google.com:19302', 'stun:global.stun.twilio.com:3478'] }
      ],
      iceTransportPolicy: forceTurn ? 'relay' : 'all'
    };

    addLog(`Creating peer connection (Force TURN: ${forceTurn})`);
    const pc = new RTCPeerConnection(configuration);
    peerConnectionRef.current = pc;

    if (localStream) {
      localStream.getTracks().forEach(track => {
        pc.addTrack(track, localStream);
      });
    }

    pc.ontrack = (event) => {
      addLog('Received remote track');
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateType = event.candidate.type || 'unknown';
        addLog(`ICE candidate: ${candidateType}`);
        sendMessage({
          type: 'ice-candidate',
          candidate: event.candidate
        });
      }
    };

    pc.onconnectionstatechange = () => {
      addLog(`Connection state: ${pc.connectionState}`);
      setConnectionState(pc.connectionState);
      
      if (pc.connectionState === 'connected') {
        addLog('✅ Peer connection established');
        startStatsMonitoring();
      } else if (pc.connectionState === 'failed') {
        addLog('❌ Connection failed');
        handleConnectionFailed();
      }
    };
    
    pc.oniceconnectionstatechange = async () => {
      addLog(`ICE state: ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        await checkSelectedPathAndEnforcePolicy();
      }
      if (['failed', 'disconnected', 'closed'].includes(pc.iceConnectionState)) {
        setSelectedPath('—');
      }
    };

    return pc;
  };

  const createOffer = async () => {
    try {
      const pc = peerConnectionRef.current;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      sendMessage({
        type: 'offer',
        offer: offer
      });
    } catch (error) {
      console.error('Error creating offer:', error);
    }
  };

  const handleOffer = async (offer) => {
    try {
      await createPeerConnection();
      const pc = peerConnectionRef.current;
      
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      sendMessage({
        type: 'answer',
        answer: answer
      });
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      const pc = peerConnectionRef.current;
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      const pc = peerConnectionRef.current;
      if (pc) {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  const checkSelectedPathAndEnforcePolicy = async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    
    try {
      const stats = await pc.getStats();
      let pair, local, remote;
      
      stats.forEach(report => {
        if (report.type === 'transport' && report.selectedCandidatePairId) {
          pair = stats.get(report.selectedCandidatePairId);
        }
      });
      
      if (!pair) {
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.nominated && report.state === 'succeeded') {
            pair = report;
          }
        });
      }
      
      if (pair) {
        local = stats.get(pair.localCandidateId);
        remote = stats.get(pair.remoteCandidateId);
      }
      
      const localType = local?.candidateType || '?';
      const remoteType = remote?.candidateType || '?';
      
      const pathDisplay = `${localType} ↔ ${remoteType}`;
      setSelectedPath(pathDisplay);
      
      addLog(`Selected path: ${pathDisplay}`);
      
      if (blockRelay && (localType === 'relay' || remoteType === 'relay')) {
        addLog('🚫 POLICY VIOLATION: TURN relay detected!');
        handleRelayDetected();
      }
    } catch (error) {
      addLog(`Error checking stats: ${error.message}`);
    }
  };
  
  const startStatsMonitoring = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }

    statsIntervalRef.current = setInterval(async () => {
      await checkSelectedPathAndEnforcePolicy();
    }, 5000);
  };

  const handleRelayDetected = () => {
    addLog('⛔ Stopping call due to TURN relay detection');
    
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }
    
    sendMessage({
      type: 'policy-block',
      reason: 'TURN relay in use – blocked by policy'
    });
    
    cleanup();
    setShowRelayWarning(true);
  };

  const handleConnectionFailed = () => {
    toast.error('Connection failed');
    setShowRelayWarning(true);
  };

  const handlePeerLeft = () => {
    toast.info('Other participant left the call');
    setRemoteStream(null);
    setIsWaitingForPeer(true);
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
  };

  const sendMessage = (message) => {
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      webSocketRef.current.send(JSON.stringify(message));
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const endCall = () => {
    cleanup();
    navigate('/');
  };

  const handleReconnect = () => {
    window.location.reload();
  };

  const handleShareLink = () => {
    setShowShareModal(true);
  };

  const copyMeetingLink = () => {
    const meetingLink = `${window.location.origin}/meet/${roomId}`;
    
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(meetingLink)
        .then(() => {
          setLinkCopied(true);
          toast.success('Meeting link copied to clipboard!');
          setTimeout(() => setLinkCopied(false), 2000);
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyTextToClipboard(meetingLink);
        });
    } else {
      // Use fallback for older browsers or blocked clipboard
      fallbackCopyTextToClipboard(meetingLink);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setLinkCopied(true);
        toast.success('Meeting link copied to clipboard!');
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        toast.error('Failed to copy. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy. Please copy manually.');
    }
    
    document.body.removeChild(textArea);
  };

  const cleanup = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    if (webSocketRef.current) {
      webSocketRef.current.close();
    }

    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-900 relative overflow-hidden">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/60 to-transparent px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-white">
            <div className="text-sm opacity-75">Room: {roomId.slice(0, 20)}...</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            connectionState === 'connected' ? 'bg-green-600' :
            connectionState === 'connecting' ? 'bg-yellow-600' :
            'bg-red-600'
          } text-white`}>
            {connectionState === 'connected' ? '● Connected' :
             connectionState === 'connecting' ? '● Connecting...' :
             '● Disconnected'}
          </div>
        </div>
      </div>

      {/* Main Video Area - Zoom Style */}
      <div className="flex-1 relative">
        {/* Remote Video (Main/Speaker View) */}
        <div className="absolute inset-0 bg-slate-950">
          {isWaitingForPeer ? (
            <div className="w-full h-full flex items-center justify-center text-white">
              <div className="text-center">
                <div className="animate-pulse mb-4">
                  <Users className="w-20 h-20 mx-auto opacity-50" />
                </div>
                <p className="text-2xl font-medium mb-2">Waiting for others to join...</p>
                <p className="text-slate-400">Share the meeting link to invite participants</p>
              </div>
            </div>
          ) : remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white">
              <div className="text-center">
                <div className="animate-pulse">
                  <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
                </div>
                <p className="text-lg">Connecting to peer...</p>
              </div>
            </div>
          )}
          
          {/* Participant Name Overlay */}
          {remoteStream && (
            <div className="absolute bottom-6 left-6 bg-black/60 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
              <p className="text-sm font-medium">Remote User</p>
            </div>
          )}
        </div>

        {/* Local Video (Self View - Thumbnail in corner like Zoom) */}
        <div className="absolute top-6 right-6 w-64 h-48 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 z-10">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs backdrop-blur-sm">
            You
          </div>
          {!isVideoEnabled && (
            <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-white opacity-50" />
            </div>
          )}
        </div>
      </div>

      {/* Bottom Controls - Auto-hide like Zoom */}
      <div className={`absolute bottom-0 left-0 right-0 z-20 transition-all duration-300 ${
        showControls ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}>
        <div className="bg-gradient-to-t from-black/90 via-black/70 to-transparent px-6 py-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {/* Left Side - Info */}
            <div className="flex items-center gap-2">
              {showDiagnostics && (
                <div className="text-white text-xs space-x-3 flex items-center">
                  <span className={`px-2 py-1 rounded ${
                    selectedPath.includes('relay') ? 'bg-red-900/50' : 'bg-green-900/50'
                  }`}>
                    {selectedPath}
                  </span>
                  <span className="opacity-75">{downlink}</span>
                  <span className="opacity-75">{networkType}</span>
                </div>
              )}
            </div>

            {/* Center - Main Controls */}
            <div className="flex items-center gap-3">
              <Button
                variant={isAudioEnabled ? 'default' : 'destructive'}
                size="lg"
                onClick={toggleAudio}
                className="rounded-full w-14 h-14 bg-slate-700 hover:bg-slate-600 border-2 border-transparent hover:border-white/20"
              >
                {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </Button>
              
              <Button
                variant={isVideoEnabled ? 'default' : 'destructive'}
                size="lg"
                onClick={toggleVideo}
                className="rounded-full w-14 h-14 bg-slate-700 hover:bg-slate-600 border-2 border-transparent hover:border-white/20"
              >
                {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </Button>
              
              <Button
                variant="destructive"
                size="lg"
                onClick={endCall}
                className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700"
              >
                <PhoneOff className="w-5 h-5" />
              </Button>
            </div>

            {/* Right Side - More Options */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShareLink}
                className="text-white hover:bg-white/10 rounded-lg px-4"
              >
                <Share2 className="w-5 h-5 mr-2" />
                <span className="text-sm">Share</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDiagnostics(!showDiagnostics)}
                className="text-white hover:bg-white/10 rounded-lg"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Diagnostics Panel - Collapsible */}
      {showDiagnostics && (
        <div className="absolute bottom-24 right-6 w-96 bg-black/90 backdrop-blur-lg rounded-lg shadow-2xl border border-white/10 z-30">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Diagnostics</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDiagnostics(false)}
                className="text-white hover:bg-white/10"
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-2 text-sm text-white/80 mb-3">
              <div className="flex justify-between">
                <span>Path:</span>
                <span className={selectedPath.includes('relay') ? 'text-red-400' : 'text-green-400'}>
                  {selectedPath}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Downlink:</span>
                <span>{downlink}</span>
              </div>
              <div className="flex justify-between">
                <span>Network:</span>
                <span>{networkType}</span>
              </div>
            </div>
            
            <label className="flex items-center gap-2 text-white text-xs mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={forceTurn}
                onChange={(e) => setForceTurn(e.target.checked)}
                className="rounded"
              />
              Force TURN (Debug Mode)
            </label>
            
            <div className="bg-slate-950 rounded p-2 max-h-32 overflow-y-auto font-mono text-xs text-white/70">
              {diagnosticLogs.slice(-10).map((log, idx) => (
                <div key={idx} className="mb-1">{log}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Share Meeting Link Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full p-8 bg-white">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Share Meeting Link
              </h2>
              <p className="text-slate-600 mb-6">
                Anyone with this link can join the meeting
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500 mb-2">Meeting Link:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/meet/${roomId}`}
                    readOnly
                    onClick={(e) => e.target.select()}
                    className="flex-1 bg-white border border-slate-300 rounded px-3 py-2 text-sm font-mono text-slate-700 cursor-pointer"
                  />
                  <Button
                    onClick={copyMeetingLink}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {linkCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Click the link to select, then copy manually if needed
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => setShowShareModal(false)}
                  variant="outline"
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Relay Warning Modal */}
      {showRelayWarning && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md p-8 bg-white">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PhoneOff className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Connection Not Supported
              </h2>
              <p className="text-slate-600 mb-6">
                This network does not support secure direct video connection.
                Please switch to <strong>Home Wi-Fi</strong> or <strong>Mobile Data</strong> and rejoin.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleReconnect}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Reconnect
                </Button>
                <Button
                  variant="outline"
                  onClick={endCall}
                  className="w-full"
                >
                  Leave Meeting
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MeetRoom;
